import { SkycFormWidgetPropTypes } from '@airtel/air-skyc-postpaid-widgets';
import faviconIcon from '../assets/images/ico/favicon.ico';
import {
   generateInteractionId,
   generateUniqueId,
   getCallUtilFunction,
   getNativeEventEmitterAction,
   getStoreValuesAction,
   prepareDisabilityValidFormMetaData,
} from './commonUtils';
import { ImageStore } from './userImageUploadUtils';

export async function prepareFormData(
   data: { [key: string]: any },
   skipFileTransformation = false,
   imageKey: string = 'imageValue'
): Promise<FormData> {
   let rawImageValue;
   const rawImageFormData = new FormData();

   // Iterate through the data object and append each key-value pair to the FormData
   for (const key in data) {
      if (key !== 'imageValue') {
         rawImageFormData.append(key, data[key]);
      }
   }

   // Append the rawImageValue separately
   if (skipFileTransformation) {
      rawImageValue = data?.[imageKey];
   } else {
      rawImageValue = await (await fetch(data?.[imageKey])).blob();
   }
   rawImageFormData.append(imageKey, rawImageValue, `${generateUniqueId()}.jpeg`);

   return rawImageFormData;
}

export function getUploadAction(
   body: any,
   onSuccessActions: any[],
   onErrorActions: any[],
   isNewSKYCJourney: boolean,
   headers: any
) {
   let apiUrl = 'skyc/ecaf-commons/api/v2/image/upload/image/v1';

   if (isNewSKYCJourney) {
      apiUrl = 'new-acq/dochub/doc-hub/upload/image';
   }

   return {
      type: 'apiCall',
      payload: {
         requestType: 'POST',
         apiUrl,
         authTokenGroup: 'SKYC_REVERIFICATION',
         ...{ headers },
         body,
         onSuccess: {
            actions: onSuccessActions,
         },
         onError: onErrorActions,
      },
   };
}

const getUploadLoaderAction = (
   uploadContainerId: string,
   isUploading: boolean,
   errorText = ''
) => {
   const uploadDataKey = `uploadsDataProps.${uploadContainerId}`;
   return {
      [`${uploadDataKey}.errorText`]: errorText,
      [`${uploadDataKey}.isUploading`]: isUploading,
   };
};

export const uploadDisabilityProof = async ({
   additionalData,
   getState,
   onUserAction,
}: any) => {
   try {
      const { storeValues } = getState();
      const { latitude, longitude } = storeValues?.userLocation ?? {};
      const { type, file } = additionalData?.dataFromWidget ?? {};
      const widgetId = 'DISABILITY_INFO';
      const isNewSKYCJourney = storeValues?.journeyContext?.journey === 'SKYC_NEW';
      const journeyType = storeValues?.appData?.lob;

      let headers = {};

      const mappingKeys: any = {
         front: {
            imageType: getImageType('PWD_FRONT', isNewSKYCJourney),
            uploadContainerId: 'uploadFrontImageDisability',
            imageUploadedKey: 'isFrontImageUploaded',
         },
         back: {
            imageType: getImageType('PWD_BACK', isNewSKYCJourney),
            uploadContainerId: 'uploadBackImageDisability',
            imageUploadedKey: 'isBackImageUploaded',
         },
      };
      const { imageType, uploadContainerId, imageUploadedKey } = mappingKeys[type];
      const circle = storeValues?.entityDetails?.circleId;

      let documentData: any = {
         imageValue: await getProcessedImage({ getState }, file),
         interactionId: generateInteractionId(),
         latitude: latitude?.toString(),
         longitude: longitude?.toString(),
         lob: 'CUSTOMER_REVERIFICATION',
         imageType,
         documentType: imageType,
         source: 'SHOP_RV',
      };
      let imageKey = 'imageValue';

      if (isNewSKYCJourney) {
         const kycId = storeValues?.skycContext?.kycId;
         imageKey = 'docFile';
         headers = {
            source: 'shop',
            'x-correlation-id': generateUniqueId(),
         };
         documentData = {
            docFile: await getProcessedImage({ getState }, file),
            docType: imageType,
            kycId,
            kycType: 'SKYC',
            channel: 'SHOP',
            circle,
            latitude,
            longitude,
            journeyType,
            requestTime: new Date().toISOString(),
         };
      }

      const body = await prepareFormData(documentData, false, imageKey);

      const startLoaderAction: SkycFormWidgetPropTypes = {
         ...getUploadLoaderAction(uploadContainerId, true),
      };

      const showSuccessEventData: SkycFormWidgetPropTypes = {
         ...getUploadLoaderAction(uploadContainerId, false),
      };

      const showErrorEventData: SkycFormWidgetPropTypes = {
         ...getUploadLoaderAction(uploadContainerId, false, 'Failed to upload image.'),
      };

      const onError = [
         getNativeEventEmitterAction(widgetId, showErrorEventData),
         getStoreValuesAction('pwdData', {
            [imageUploadedKey]: false,
         }),
      ];

      const validStepData: any[] = prepareDisabilityValidFormMetaData({
         additionalData,
         getState,
         onUserAction,
      })

      const onSuccessEventData = [
         getNativeEventEmitterAction(widgetId, showSuccessEventData),
         getStoreValuesAction('pwdData', {
            [imageUploadedKey]: true,
         }),
         ...validStepData
      ];

      const onSuccess = [
         getCallUtilFunction('onSuccessUpload', [
            {
               onSuccess: onSuccessEventData,
               onError,
            },
         ]),
      ];

      onUserAction({
         id: 'uploadDisabilityProof',
         actions: [
            getStoreValuesAction('pwdData', {
               [imageUploadedKey]: false,
            }),
            getNativeEventEmitterAction(widgetId, startLoaderAction),
            getUploadAction(body, onSuccess, onError, isNewSKYCJourney, headers),
         ],
      });
   } catch (e) {
      console.error('Error during uploading file');
   }
};

const getImageType = (type: string, isNewSKYCJourney: boolean) => {
   return isNewSKYCJourney ? type : `${type}_IMAGE`;
};

export const uploadPOAAndPOIProof = async ({
   args,
   additionalData,
   getState,
   onUserAction,
}: any) => {
   try {
      const { storeValues } = getState();
      const { latitude, longitude } = storeValues?.userLocation ?? {};
      const { type, file } = additionalData?.dataFromWidget ?? {};
      const imageSameAsPoa = args?.[1];
      const widgetId = 'IDENTITY_PROOF';
      const isNewSKYCJourney = storeValues?.journeyContext?.journey === 'SKYC_NEW';
      const circle = storeValues?.entityDetails?.circleId;
      const journeyType = storeValues?.appData?.lob;
      const mappingKeys: any = {
         addressFront: {
            imageType: getImageType('REFEREE_POA_FRONT', isNewSKYCJourney),
            uploadContainerId: 'uploadFrontAddressProof',
            imageUploadedKey: 'isFrontAddressProofUploaded',
         },
         addressBack: {
            imageType: getImageType('REFEREE_POA_BACK', isNewSKYCJourney),
            uploadContainerId: 'uploadBackAddressProof',
            imageUploadedKey: 'isBackAddressProofUploaded',
         },
         identityFront: {
            imageType: getImageType('REFEREE_POI_FRONT', isNewSKYCJourney),
            uploadContainerId: 'uploadFrontIdProof',
            imageUploadedKey: 'isFrontIdProofUploaded',
         },
         identityBack: {
            imageType: getImageType('REFEREE_POI_BACK', isNewSKYCJourney),
            uploadContainerId: 'uploadBackIdProof',
            imageUploadedKey: 'isBackIdProofUploaded',
         },
      };
      const { imageType, uploadContainerId, imageUploadedKey } = mappingKeys[type];

      let documentData: any = {
         imageValue: await getProcessedImage({ getState }, file),
         interactionId: generateInteractionId(),
         latitude: latitude?.toString(),
         longitude: longitude?.toString(),
         lob: 'CUSTOMER_REVERIFICATION',
         imageType,
         documentType: imageType,
         source: 'SHOP_RV',
         ...(imageSameAsPoa && { imageSameAsPoa }),
      };

      let imageKey = 'imageValue';
      let headers = {};

      if (isNewSKYCJourney) {
         imageKey = 'docFile';

         const kycId = generateInteractionId();
         headers = {
            source: 'shop',
            'x-correlation-id': generateUniqueId(),
         };

         documentData = {
            docFile: await getProcessedImage({ getState }, file),
            docType: imageType,
            kycId: kycId,
            kycType: 'SKYC',
            channel: 'SHOP',
            circle,
            latitude,
            longitude,
            journeyType,
            requestTime: new Date().toISOString(),
         };
      }

      const body = await prepareFormData(documentData, false, imageKey);

      const startLoaderAction: SkycFormWidgetPropTypes = {
         ...getUploadLoaderAction(uploadContainerId, true),
      };

      const showSuccessEventData: SkycFormWidgetPropTypes = {
         ...getUploadLoaderAction(uploadContainerId, false),
      };

      const showErrorEventData: SkycFormWidgetPropTypes = {
         ...getUploadLoaderAction(uploadContainerId, false, 'Failed to upload image.'),
      };

      const onError = [
         getNativeEventEmitterAction(widgetId, showErrorEventData),
         getStoreValuesAction('identityData', {
            [imageUploadedKey]: false,
         }),
      ];

      const onSuccessEventData = [
         getNativeEventEmitterAction(widgetId, showSuccessEventData),
         getStoreValuesAction('identityData', {
            [imageUploadedKey]: true,
         }),
      ];

      const onSuccess = [
         getCallUtilFunction('onSuccessUpload', [
            {
               onSuccess: onSuccessEventData,
               onError,
            },
         ]),
      ];

      onUserAction({
         id: 'uploadPOAAndPOIProof',
         actions: [
            getStoreValuesAction('identityData', {
               [imageUploadedKey]: false,
            }),
            getNativeEventEmitterAction(widgetId, startLoaderAction),
            getUploadAction(body, onSuccess, onError, isNewSKYCJourney, headers),
         ],
      });
   } catch (e) {
      console.error('Error during uploading file');
   }
};

export const onSuccessUpload = ({ args, additionalData, onUserAction, getState }: any) => {
   const { storeValues = {} } = getState();
   const { journeyContext = {} } = storeValues;
   const { journey = "" } = journeyContext;
   const isNewSKYCJourney = journey === "SKYC_NEW";
   const status = additionalData?.dataFromWidget?.response?.status;
   const isSuccess = isNewSKYCJourney
      ? status === '200'
      : status?.code === '200';

   if (isSuccess) {
      onUserAction({
         id: 'ON_SUCCESS_UPLOAD_UTIL_SUCCESS',
         actions: args?.[0]?.onSuccess,
      });
   } else {
      onUserAction({
         id: 'ON_SUCCESS_UPLOAD_UTIL_ERROR',
         actions: args?.[0]?.onError,
      });
   }
};

export const savePOIImages = async ({ additionalData }: any) => {
   const { type, file } = additionalData?.dataFromWidget ?? {};

   const imageStore = ImageStore.getInstance();
   imageStore.setItem(type, file);
};

const getProcessedImage = ({ getState }: any, file: any) => {
   return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = function (e) {
         try {
            if (!(typeof e?.target?.result === 'string')) {
               reject();
            } else {
               const imageObj = new Image();

               imageObj.onload = async function () {
                  const canvas = document.createElement('canvas');
                  const context = canvas?.getContext('2d');

                  canvas.width = imageObj.width;
                  canvas.height = imageObj.height;

                  if (context && canvas.width && canvas.height) {
                     const { storeValues } = getState();
                     const { latitude = '', longitude = '' } = storeValues?.userLocation;
                     const latLong =
                        !!latitude && !!longitude ? `${latitude}, ${longitude} ` : '';
                     const { siNumberStatusDetails, journeyContext, skycContext } =
                        storeValues ?? {};
                     const isNewSKYCJourney = journeyContext?.journey === 'SKYC_NEW';
                     let caFNumber = siNumberStatusDetails?.cafNumber;
                     const line2Text = `${latLong}${caFNumber ? ' ' + caFNumber : ''}`;
                     const textXPosition = 10;
                     const result = await fetch(faviconIcon);
                     const blob = await result.blob();
                     const image = await createImageBitmap(blob);

                     if (isNewSKYCJourney) {
                        caFNumber = skycContext?.cafNumber;
                     }

                     context.clearRect(0, 0, canvas.width, canvas.height);
                     context.drawImage(
                        imageObj,
                        0,
                        0,
                        imageObj.width,
                        imageObj.height,
                        0,
                        0,
                        imageObj.width,
                        imageObj.height
                     );
                     context.fillStyle = 'white';
                     context.font = 'regular 25px Arial';
                     context.fillText(
                        new Date().toLocaleString(),
                        textXPosition,
                        canvas.height - 35
                     );
                     context.fillText(line2Text, textXPosition, canvas.height - 20);
                     context.translate(textXPosition, canvas.height - 80);
                     context.drawImage(image, 0, 0, 20, 20);
                     context.fill();

                     resolve(canvas.toDataURL('image/jpeg'));
                  }
               };

               imageObj.src = e?.target?.result;
            }
         } catch (error) {
            reject(error);
         }
      };

      reader.readAsDataURL(file);
   });
};
